import type { AudioEvent, SoundClass } from './events.js';
import { Howl } from 'howler';
import type { RegistryEntry } from './soundRegistry.js';

/**
 * Audio playback surface consumed by AudioDirector. The Director never
 * touches Howler directly — it dispatches `play` / `stop` against this
 * interface, which lets the unit tests inject a fake recorder.
 *
 * `HowlerAudioPlayer` (below in Task 5) is the production implementation.
 */
export interface AudioPlayer {
  /** Begin playing the registered sample for `event`. If `src === null`
   *  or the event's class volume is currently 0, this is a silent no-op. */
  play(event: AudioEvent): void;

  /** Stop a currently-playing sample. Idempotent (safe to call when nothing
   *  is playing). Only meaningfully used for `lobby` today (cut the bed if
   *  the player leaves the lobby before it finishes). */
  stop(event: AudioEvent): void;

  /** Update the master multiplier for a sound class. The Director calls
   *  this whenever `preferences.musicVolume` / `soundsVolume` changes. */
  setVolume(soundClass: SoundClass, value: number): void;

  /** Release any held resources. Called by `useAudioDirector` on unmount. */
  dispose(): void;
}

/**
 * Production AudioPlayer using Howler.js.
 *
 * Howler handles the audio-context unlock on the first user gesture
 * (Discord iframe + iOS); we don't manage that ourselves.
 *
 * The constructor takes an optional `HowlCtor` so tests can inject a fake
 * (jsdom has no working AudioContext; the real Howl logs warnings).
 */
type HowlCtor = new (opts: { src: string[]; loop?: boolean }) => {
  play(): number;
  stop(): void;
  volume(value: number): void;
  unload(): void;
};

export class HowlerAudioPlayer implements AudioPlayer {
  private howls = new Map<AudioEvent, InstanceType<HowlCtor>>();
  private classVolumes: Record<SoundClass, number> = { music: 1, sounds: 1 };

  constructor(
    private readonly registry: Record<AudioEvent, RegistryEntry>,
    private readonly HowlCtor: HowlCtor = Howl as unknown as HowlCtor,
  ) {}

  play(event: AudioEvent): void {
    const entry = this.registry[event];
    if (entry.src === null) return;
    if (this.classVolumes[entry.class] === 0) return;
    let howl = this.howls.get(event);
    if (howl === undefined) {
      howl = new this.HowlCtor({ src: [entry.src], loop: entry.loop ?? false });
      this.howls.set(event, howl);
    }
    howl.volume(this.effectiveVolume(entry));
    howl.play();
  }

  stop(event: AudioEvent): void {
    const howl = this.howls.get(event);
    if (howl === undefined) return;
    howl.stop();
  }

  setVolume(soundClass: SoundClass, value: number): void {
    this.classVolumes[soundClass] = value;
    for (const [event, howl] of this.howls) {
      const entry = this.registry[event];
      if (entry.class === soundClass) {
        howl.volume(this.effectiveVolume(entry));
      }
    }
  }

  dispose(): void {
    for (const howl of this.howls.values()) {
      howl.stop();
      /* unload() removes the instance from Howler's global registry and
         frees its decoded buffer — stop() alone leaves both behind, so the
         buffers leaked across Director remounts (StrictMode double-mount,
         session swaps). */
      howl.unload();
    }
    this.howls.clear();
  }

  private effectiveVolume(entry: RegistryEntry): number {
    return (entry.baseVolume ?? 1) * this.classVolumes[entry.class];
  }
}
